import type { Locale } from '@/utils/localeFormatters';

import { formatDashboardNumber } from './dashboardNumber';

/**
 * What joins a number to its "%": nothing in en, a no-break space in nb and es.
 * `LOCALE_RULES.percentageSpacing` has the same split but an ordinary space, which lets
 * "22 %" break across two lines.
 */
const PERCENT_JOIN: Record<Locale, string> = {
    en: '',
    nb: '\u00A0',
    es: '\u00A0',
};

export interface PercentFormatOptions {
    /** The number locale, from `dashboardLocale` (U3). */
    locale: Locale;
    /** Prefix "+" on a value above zero, for a difference such as feed vs plan. */
    signed?: boolean;
}

/**
 * A percentage (22 for 22%) with `digits` decimals, or "–" when there is none.
 *
 * The sign is decided on the rounded figure, so 0.3 and -0.3 at no decimals both print
 * "0%" and never "+0%" or "-0%": a reader would take a signed zero for a real difference.
 * A negative keeps the locale's own minus sign (U+2212 in nb), as its other numbers do.
 */
export function formatPercent(
    value: number | null | undefined,
    digits: number,
    { locale, signed = false }: PercentFormatOptions
): string {
    if (value == null || !Number.isFinite(value)) return '–';
    const fractionDigits = { minimumFractionDigits: digits, maximumFractionDigits: digits };
    const magnitude = formatDashboardNumber(Math.abs(value), locale, fractionDigits);
    const suffix = `${PERCENT_JOIN[locale]}%`;
    if (!hasNonZeroDigit(magnitude)) return `${magnitude}${suffix}`;
    if (value < 0) return `${formatDashboardNumber(value, locale, fractionDigits)}${suffix}`;
    return `${signed ? '+' : ''}${magnitude}${suffix}`;
}

/**
 * The value as a reader takes it from its printed figure: zero when `formatPercent` prints
 * it as a zero at `digits`, the value itself otherwise. A tone or title decided on this
 * never claims a difference the figure beside it does not show.
 */
export function displayedPercent(value: number | null, digits: number): number | null {
    if (value == null || !Number.isFinite(value)) return null;
    const magnitude = formatDashboardNumber(Math.abs(value), 'en', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
    return hasNonZeroDigit(magnitude) ? value : 0;
}

function hasNonZeroDigit(figure: string): boolean {
    return /[1-9]/.test(figure);
}
